import { useEffect, useState } from "react";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import moment from "moment";
import "moment/locale/pt-br";
import XLSX from "xlsx-js-style";
import toast from "react-hot-toast";

// Material UI imports
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import WarningIcon from "@mui/icons-material/Warning";
import PeopleIcon from "@mui/icons-material/People";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import UpdateIcon from "@mui/icons-material/Update";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Link } from "react-router-dom";

import ListaFaltas from "./ListaFaltas";

// Configurar locale do moment para português
moment.locale("pt-br");

// Componentes estilizados
const SummaryCard = styled(Card)(({ theme, cardtype }) => ({
  height: "100%",
  backgroundColor:
    cardtype === "taxa"
      ? "rgba(255, 152, 0, 0.08)"
      : cardtype === "horarios"
      ? "rgba(33, 150, 243, 0.08)"
      : cardtype === "dias"
      ? "rgba(76, 175, 80, 0.08)"
      : cardtype === "genero"
      ? "rgba(156, 39, 176, 0.08)"
      : cardtype === "tratamentos"
      ? "rgba(211, 47, 47, 0.08)"
      : "rgba(63, 81, 181, 0.08)",
  borderLeft: `4px solid ${
    cardtype === "taxa"
      ? theme.palette.warning.main
      : cardtype === "horarios"
      ? theme.palette.info.main
      : cardtype === "dias"
      ? theme.palette.success.main
      : cardtype === "genero"
      ? theme.palette.secondary.main
      : cardtype === "tratamentos"
      ? theme.palette.error.main
      : theme.palette.primary.main
  }`,
}));

/* ---------------------------------------------------------------------------
 * Recomendações da IA — DESLIGADAS temporariamente em 2026-08-18.
 *
 * O modelo (Ollama, `qwen3:4b`) acabava sempre em timeout. E o custo não era só
 * a caixa de recomendações ficar vazia: a chamada era disparada AUTOMATICAMENTE
 * ao gerar o relatório, por isso o relatório inteiro ficava à espera até três
 * minutos por algo que nunca chegava.
 *
 * Com isto a `false`: não se chama a IA e a secção não aparece. O relatório de
 * faltas em si não muda.
 *
 * Para voltar a ligar: pôr `true` aqui, no `RelatorioFaltas.js` ao lado, e no
 * `RelatorioFaltas.jsx` da v2. Não apagar o código — está inteiro e a
 * funcionar; o que falha é o modelo.
 * ------------------------------------------------------------------------- */
const RECOMENDACOES_IA_ATIVAS = false;

function RelatorioFaltasAgendaTratamento({
  dataFiltro,
  clinicaId,
  auth,
  acao,
  sendRequest,
  atualizacoes,
  // Unidade escolhida no topo do ecrã. Vazio = consolidado da empresa.
  unidadeSelecionada,
}) {
  const { startLoading, stopLoading } = useLoading();
  const [dados, setDados] = useState(null);
  const [error, setError] = useState(null);

  const [recomendacoes, setRecomendacoes] = useState(null);
  const [loadingRecomendacoes, setLoadingRecomendacoes] = useState(false);
  const [errorRecomendacoes, setErrorRecomendacoes] = useState(null);

  // Este useEffect executa quando o componente é montado ou quando a ação muda
  useEffect(() => {
    // Se uma ação foi especificada, execute-a
    if (acao) {
      if (acao === "gerar") {
        gerarRelatorio();
      } else if (acao === "exportar") {
        exportarFicheiro();
      }
    }
  }, [acao, atualizacoes]);

  const gerarRelatorio = async () => {
    if (!clinicaId) return;

    setError(null);

    setRecomendacoes(null); // Limpar recomendações anteriores
    setErrorRecomendacoes(null);

    startLoading();
    try {
      const formData = new FormData();
      formData.append("clinicaId", clinicaId);
      formData.append(
        "dataInicio",
        dataFiltro.dataInicial.format("YYYY-MM-DD")
      );
      formData.append("dataFim", dataFiltro.dataFinal.format("YYYY-MM-DD"));
      // Vazio NÃO se envia: no servidor, a ausência significa «as unidades
      // deste utilizador», que é o consolidado. Enviar "" seria pedir uma
      // unidade chamada vazio.
      if (unidadeSelecionada) {
        formData.append("estabelecimento", unidadeSelecionada);
      }

      // Chamada para a API
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/agendatratamento/gerarRelatorioFaltas`,
        "POST",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      // Atualiza o estado com os dados recebidos
      setDados(responseData);

      // Sem esta guarda, o relatório ficava à espera até 3 minutos por um
      // modelo que nunca respondia. Ver `RECOMENDACOES_IA_ATIVAS`.
      if (RECOMENDACOES_IA_ATIVAS)
        gerarRecomendacoesIA(responseData.estatisticas);
    } catch (err) {
      console.error("Erro ao gerar relatório de faltas:", err);
      setError(
        err.message || "Ocorreu um erro ao gerar o relatório de faltas."
      );
    } finally {
      stopLoading();
    }
  };

  const gerarRecomendacoesIA = async (estatisticas) => {
    setLoadingRecomendacoes(true);
    setErrorRecomendacoes(null);

    try {
      const requestBody = {
        missedRate: parseFloat(estatisticas.taxaFaltas) || 0,
        missedTimeSlots: estatisticas.horariosComMaisFaltas || [],
        missedWeekdays: estatisticas.diasComMaisFaltas || [],
        missedByGender: estatisticas.faltasPorGenero || [],
        missedByAgeGroup: estatisticas.faltasPorFaixaEtaria || [],
        missedByTimeOfDay: estatisticas.faltasPorTurno || [],
        missedByWeekOfMonth: estatisticas.faltasPorSemanaDoMes || [],
        missedByBookingAdvance: estatisticas.faltasPorAntecedencia || [],
        reportedReasons: ["Não informado"], // Ajustar se tiver dados reais
        neighborhoodsWithHighestMissedRates: ["Desconhecido"], // Ajustar se tiver dados reais
      };

      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/llama/gerarRecomendacoesFaltasIA`,
        "POST",
        JSON.stringify(requestBody),
        {
          Authorization: "Bearer " + auth.token,
          "Content-Type": "application/json",
        }
      );

      setRecomendacoes(response.recomendacoes);
    } catch (err) {
      console.error("Erro ao gerar recomendações de IA:", err);
      setErrorRecomendacoes(
        err.message || "Erro ao gerar recomendações. Tente novamente."
      );
    } finally {
      setLoadingRecomendacoes(false);
    }
  };

  const exportarFicheiro = () => {
    if (!dados) {
      toast.error("É necessário gerar o relatório primeiro!");
      return;
    }

    try {
      // Criar um novo workbook
      const wb = XLSX.utils.book_new();

      // Criar planilha de resumo
      const resumoData = [
        ["RELATÓRIO DE FALTAS"],
        [
          `Período: ${dataFiltro.dataInicial.format(
            "DD/MM/YYYY"
          )} a ${dataFiltro.dataFinal.format("DD/MM/YYYY")}`,
        ],
        [`Clínica: ${dados.clinica.nome || ""}`],
        [],
        ["ESTATÍSTICAS GERAIS"],
        ["Taxa de Faltas", dados.estatisticas.taxaFaltas],
        ["Total de Faltas", dados.estatisticas.totalFaltas],
        ["Total de Tratamentos", dados.estatisticas.totalTratamentos],
        [],
        ["HORÁRIOS COM MAIS FALTAS"],
        ...dados.estatisticas.horariosComMaisFaltas.map((horario) => [horario]),
        [],
        ["DIAS COM MAIS FALTAS"],
        ...dados.estatisticas.diasComMaisFaltas.map((dia) => [dia]),
        [],
        ["FALTAS POR GÊNERO"],
        ...dados.estatisticas.faltasPorGenero.map((genero) => [genero]),
        [],
        ["FALTAS POR FAIXA ETÁRIA"],
        ...dados.estatisticas.faltasPorFaixaEtaria.map((faixa) => [faixa]),
        [],
        ["TRATAMENTOS MAIS FALTADOS"],
        ...(
          dados.estatisticas.tratamentosMaisFaltados || ["Dados insuficientes"]
        ).map((tratamento) => [tratamento]),
        [],
        ["FALTAS POR ANTECEDÊNCIA DE AGENDAMENTO"],
        ...(
          dados.estatisticas.faltasPorAntecedencia || ["Dados insuficientes"]
        ).map((antecedencia) => [antecedencia]),
      ];

      const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);

      // Criar planilha com detalhes dos médicos
      const medicosHeader = [
        ["Médico", "Total Agendamentos", "Faltas", "Taxa"],
      ];

      const medicosData = dados.estatisticas.taxaFaltasPorMedico.map(
        (medico) => [medico.nome, medico.total, medico.faltas, medico.taxa]
      );

      const wsMedicos = XLSX.utils.aoa_to_sheet([
        ...medicosHeader,
        ...medicosData,
      ]);

      // Criar planilha com clientes
      const clientesHeader = [
        ["Nome", "Contato", "Total Faltas", "Gênero", "Idade"],
      ];

      const clientesData = dados.estatisticas.clientesComMaisFaltas.map(
        (cliente) => [
          cliente.nome,
          cliente.contato,
          cliente.totalFaltas,
          cliente.genero,
          cliente.idade,
        ]
      );

      const wsClientes = XLSX.utils.aoa_to_sheet([
        ...clientesHeader,
        ...clientesData,
      ]);

      // Criar planilha com recomendações
      const recomendacoesData = [
        ["RECOMENDAÇÕES DA IA PARA REDUÇÃO DE FALTAS"],
        [],
        ...(recomendacoes
          ? recomendacoes.split("\n").map((linha) => [linha.trim()])
          : [
              [
                RECOMENDACOES_IA_ATIVAS
                  ? "Recomendações não disponíveis. Aguarde o processamento ou gere novamente."
                  : "Recomendações de IA temporariamente desativadas.",
              ],
            ]),
      ];

      const wsRecomendacoes = XLSX.utils.aoa_to_sheet(recomendacoesData);

      // Aplicar estilos
      // Título
      wsResumo["A1"].s = {
        font: { bold: true, sz: 16 },
        alignment: { horizontal: "center" },
      };

      // Cabeçalhos de seção
      wsResumo["A5"].s = {
        font: { bold: true, sz: 14 },
        fill: { fgColor: { rgb: "E6E6E6" } },
      };
      wsResumo["A10"].s = {
        font: { bold: true, sz: 14 },
        fill: { fgColor: { rgb: "E6E6E6" } },
      };
      wsResumo["A13"].s = {
        font: { bold: true, sz: 14 },
        fill: { fgColor: { rgb: "E6E6E6" } },
      };
      wsResumo["A16"].s = {
        font: { bold: true, sz: 14 },
        fill: { fgColor: { rgb: "E6E6E6" } },
      };
      wsResumo["A19"].s = {
        font: { bold: true, sz: 14 },
        fill: { fgColor: { rgb: "E6E6E6" } },
      };
      wsResumo["A22"].s = {
        font: { bold: true, sz: 14 },
        fill: { fgColor: { rgb: "E6E6E6" } },
      };
      wsResumo["A25"].s = {
        font: { bold: true, sz: 14 },
        fill: { fgColor: { rgb: "E6E6E6" } },
      };

      // Cabeçalhos de tabelas
      for (let i = 0; i < medicosHeader[0].length; i++) {
        const cell = XLSX.utils.encode_cell({ r: 0, c: i });
        wsMedicos[cell].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center" },
        };
      }

      for (let i = 0; i < clientesHeader[0].length; i++) {
        const cell = XLSX.utils.encode_cell({ r: 0, c: i });
        wsClientes[cell].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center" },
        };
      }

      // Título da recomendação
      wsRecomendacoes["A1"].s = {
        font: { bold: true, sz: 16 },
        alignment: { horizontal: "center" },
      };

      // Adicionar planilhas ao workbook
      XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");
      XLSX.utils.book_append_sheet(wb, wsMedicos, "Taxa de Faltas por Médico");
      XLSX.utils.book_append_sheet(wb, wsClientes, "Clientes com Mais Faltas");
      XLSX.utils.book_append_sheet(wb, wsRecomendacoes, "Recomendações");

      // Configurar tamanhos de coluna
      wsResumo["!cols"] = [{ wch: 40 }, { wch: 15 }];
      wsMedicos["!cols"] = [
        { wch: 40 }, // Médico
        { wch: 20 }, // Total
        { wch: 15 }, // Faltas
        { wch: 15 }, // Taxa
      ];
      wsClientes["!cols"] = [
        { wch: 40 }, // Nome
        { wch: 20 }, // Contato
        { wch: 15 }, // Faltas
        { wch: 15 }, // Gênero
        { wch: 10 }, // Idade
      ];
      wsRecomendacoes["!cols"] = [
        { wch: 100 }, // Recomendações
      ];

      // Configurar merges
      wsResumo["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }, // Título
        { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } }, // Período
        { s: { r: 2, c: 0 }, e: { r: 2, c: 1 } }, // Clínica
      ];

      wsRecomendacoes["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } }, // Título
      ];

      // Salvar o arquivo
      XLSX.writeFile(
        wb,
        `relatorio_faltas_${moment().format("YYYY-MM-DD")}.xlsx`
      );

      toast.success("Arquivo exportado com sucesso!");
    } catch (err) {
      console.error("Erro ao exportar arquivo:", err);
      toast.error("Erro ao exportar arquivo");
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          component="div"
          sx={{ fontWeight: "bold" }}
        >
          Relatório de Faltas
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Período: {dataFiltro.dataInicial.format("DD/MM/YYYY")} a{" "}
            {dataFiltro.dataFinal.format("DD/MM/YYYY")}
          </Typography>
          {dados && (
            <Typography variant="subtitle1">
              Clínica: {dados.clinica?.nome || ""}
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!dados && !error && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {dados && (
          <>
            {/* Cards de resumo - primeira linha */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard cardtype="taxa">
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <WarningIcon color="warning" sx={{ mr: 1 }} />
                      <Typography variant="h6">Taxa de Faltas</Typography>
                    </Box>
                    <Typography
                      variant="h4"
                      component="div"
                      color="warning.main"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      {dados.estatisticas.taxaFaltas}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {dados.estatisticas.totalFaltas} faltas em{" "}
                      {dados.estatisticas.totalTratamentos} agendamentos
                    </Typography>
                  </CardContent>
                </SummaryCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard cardtype="horarios">
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <AccessTimeIcon color="info" sx={{ mr: 1 }} />
                      <Typography variant="h6">Horários</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Horários com mais faltas:</strong>
                    </Typography>
                    <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                      {dados.estatisticas.horariosComMaisFaltas
                        .slice(0, 3)
                        .map((horario, idx) => (
                          <li key={idx}>{horario}</li>
                        ))}
                    </ul>
                  </CardContent>
                </SummaryCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard cardtype="dias">
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <CalendarTodayIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="h6">Dias da Semana</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Dias com mais faltas:</strong>
                    </Typography>
                    <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                      {dados.estatisticas.diasComMaisFaltas
                        .slice(0, 3)
                        .map((dia, idx) => (
                          <li key={idx}>{dia}</li>
                        ))}
                    </ul>
                  </CardContent>
                </SummaryCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard cardtype="genero">
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <PeopleIcon color="secondary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Perfil</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Faltas por gênero:</strong>
                    </Typography>
                    <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                      {dados.estatisticas.faltasPorGenero
                        .slice(0, 2)
                        .map((genero, idx) => (
                          <li key={idx}>{genero}</li>
                        ))}
                    </ul>
                  </CardContent>
                </SummaryCard>
              </Grid>
            </Grid>

            {/* Cards de resumo - nova linha para novos dados */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={4}>
                <SummaryCard cardtype="tratamentos">
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <MedicalServicesIcon color="error" sx={{ mr: 1 }} />
                      <Typography variant="h6">Tratamentos</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Tratamentos mais faltados:</strong>
                    </Typography>
                    <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                      {(
                        dados.estatisticas.tratamentosMaisFaltados || [
                          "Dados insuficientes",
                        ]
                      )
                        .slice(0, 4)
                        .map((tratamento, idx) => (
                          <li key={idx}>{tratamento}</li>
                        ))}
                    </ul>
                  </CardContent>
                </SummaryCard>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <SummaryCard cardtype="antecedencia">
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <UpdateIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Antecedência</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Faltas por antecedência (%):</strong>
                    </Typography>
                    <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                      {(
                        dados.estatisticas.faltasPorAntecedencia || [
                          "Dados insuficientes",
                        ]
                      ).map((antecedencia, idx) => (
                        <li key={idx}>{antecedencia}</li>
                      ))}
                    </ul>
                  </CardContent>
                </SummaryCard>
              </Grid>

              <Grid item xs={12} sm={12} md={4}>
                <SummaryCard cardtype="tendencia">
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Análise de Tendência</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Correlação faltas vs. antecedência:</strong>
                    </Typography>
                    <Box
                      sx={{
                        p: 1,
                        mt: 1,
                        bgcolor: "background.paper",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {dados.estatisticas.faltasPorAntecedencia &&
                        dados.estatisticas.faltasPorAntecedencia.find((item) =>
                          item.includes("Mais de 1 mês")
                        )
                          ? "Os dados indicam que consultas agendadas com maior antecedência têm maior probabilidade de resultar em faltas."
                          : "Dados insuficientes para análise conclusiva de tendência."}
                      </Typography>
                    </Box>
                  </CardContent>
                </SummaryCard>
              </Grid>
            </Grid>

            {/* Faltas por Motivo — a única análise que diz PORQUÊ. As outras
                dizem onde e quando. "Não registado" aparece por último mesmo
                sendo frequente: é ausência de dados, não uma causa. */}
            {dados.estatisticas.faltasPorMotivo?.length > 0 && (
              <Paper sx={{ p: 2, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Motivos das Faltas
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Motivo</TableCell>
                        <TableCell align="right">Faltas</TableCell>
                        <TableCell align="right">% das faltas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dados.estatisticas.faltasPorMotivo.map((motivo) => (
                        <TableRow key={motivo.tipo} hover>
                          <TableCell
                            sx={{
                              fontStyle:
                                motivo.tipo === "NAO_REGISTADO"
                                  ? "italic"
                                  : "normal",
                              color:
                                motivo.tipo === "NAO_REGISTADO"
                                  ? "text.secondary"
                                  : "text.primary",
                            }}
                          >
                            {motivo.nome}
                          </TableCell>
                          <TableCell align="right">{motivo.total}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold" }}>
                            {motivo.percentagem}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
            {/* Faltas por Unidade — só faz sentido mostrar quando há mais do
                que uma. Numa clínica de unidade única seria uma tabela de uma
                linha a repetir o total que está em cima. */}
            {dados.estatisticas.faltasPorUnidade?.length > 1 && (
              <Paper sx={{ p: 2, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Taxa de Faltas por Unidade
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Unidade</TableCell>
                        <TableCell align="right">Total Agendamentos</TableCell>
                        <TableCell align="right">Faltas</TableCell>
                        <TableCell align="right">Taxa</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dados.estatisticas.faltasPorUnidade.map((unidade) => (
                        <TableRow key={unidade.id} hover>
                          <TableCell>
                            {unidade.nome}
                            {unidade.codigo ? ` (${unidade.codigo})` : ""}
                          </TableCell>
                          <TableCell align="right">
                            {unidade.totalMarcacoes}
                          </TableCell>
                          <TableCell align="right">
                            {unidade.totalFaltas}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: "bold",
                              color:
                                unidade.taxaFaltas > 20
                                  ? "error.main"
                                  : unidade.taxaFaltas > 10
                                    ? "warning.main"
                                    : "success.main",
                            }}
                          >
                            {unidade.taxaFaltas}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
            {/* Taxa de Faltas por Médico */}
            <Paper sx={{ p: 2, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Taxa de Faltas por Médico
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Médico</TableCell>
                      <TableCell align="right">Total Agendamentos</TableCell>
                      <TableCell align="right">Faltas</TableCell>
                      <TableCell align="right">Taxa</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dados.estatisticas.taxaFaltasPorMedico.map(
                      (medico, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{medico.nome}</TableCell>
                          <TableCell align="right">{medico.total}</TableCell>
                          <TableCell align="right">{medico.faltas}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: "bold",
                              color:
                                parseFloat(medico.taxa) > 20
                                  ? "error.main"
                                  : parseFloat(medico.taxa) > 10
                                  ? "warning.main"
                                  : "success.main",
                            }}
                          >
                            {medico.taxa}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Pacientes com Mais Faltas */}
            <Paper sx={{ p: 2, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Pacientes com Mais Faltas
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Contato</TableCell>
                      <TableCell align="right">Total Faltas</TableCell>
                      <TableCell>Gênero</TableCell>
                      <TableCell align="right">Idade</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dados.estatisticas.clientesComMaisFaltas.map(
                      (cliente, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Link
                              to={`/clientes/${cliente._id}`}
                              style={{
                                color: "#1976d2",
                                textDecoration: "none",
                                fontWeight: 500,
                              }}
                            >
                              {cliente.nome}
                            </Link>
                          </TableCell>
                          <TableCell>{cliente.contato}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: "bold",
                              color:
                                cliente.totalFaltas > 3
                                  ? "error.main"
                                  : cliente.totalFaltas > 1
                                  ? "warning.main"
                                  : "text.primary",
                            }}
                          >
                            {cliente.totalFaltas}
                          </TableCell>
                          <TableCell>{cliente.genero}</TableCell>
                          <TableCell align="right">{cliente.idade}</TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Nova seção: Análise de Antecedência */}
            <Paper sx={{ p: 2, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Análise de Antecedência vs. Faltas
              </Typography>
              <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
                <Typography variant="body1" paragraph>
                  <strong>Conclusão:</strong>{" "}
                  {dados.estatisticas.faltasPorAntecedencia &&
                  dados.estatisticas.faltasPorAntecedencia.find((item) =>
                    item.includes("Mais de 1 mês")
                  ) &&
                  dados.estatisticas.faltasPorAntecedencia.find((item) =>
                    item.includes("Mesma semana")
                  )
                    ? (() => {
                        const mesmaSemana =
                          dados.estatisticas.faltasPorAntecedencia.find(
                            (item) => item.includes("Mesma semana")
                          );
                        const maisDeMes =
                          dados.estatisticas.faltasPorAntecedencia.find(
                            (item) => item.includes("Mais de 1 mês")
                          );

                        const percentMesmaSemana = parseFloat(
                          mesmaSemana.match(/(\d+\.\d+)%/)?.[1] || 0
                        );
                        const percentMaisDeMes = parseFloat(
                          maisDeMes.match(/(\d+\.\d+)%/)?.[1] || 0
                        );

                        if (percentMaisDeMes > percentMesmaSemana) {
                          return "Os dados confirmam que agendamentos feitos com maior antecedência (mais de um mês) têm maior probabilidade de resultar em faltas. Recomenda-se implementar um sistema de lembretes específico para estes agendamentos.";
                        } else {
                          return "A análise não mostra correlação significativa entre a antecedência do agendamento e a probabilidade de faltas.";
                        }
                      })()
                    : "Dados insuficientes para uma análise conclusiva sobre a relação entre antecedência de agendamento e probabilidade de faltas."}
                </Typography>
                <Typography variant="body1">
                  <strong>Recomendação:</strong>{" "}
                  {dados.estatisticas.faltasPorAntecedencia &&
                  dados.estatisticas.faltasPorAntecedencia.find((item) =>
                    item.includes("Mais de 1 mês")
                  )
                    ? "Implementar confirmação obrigatória 48h antes da consulta para pacientes que agendaram com mais de um mês de antecedência. Enviar lembretes escalonados: 7 dias, 3 dias e 24 horas antes."
                    : "Manter o sistema atual de lembretes e monitore os dados para identificar padrões emergentes."}
                </Typography>
              </Box>
            </Paper>

            {/* Nova seção: Análise de Tratamentos */}
            <Paper sx={{ p: 2, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Análise de Tratamentos Mais Faltados
              </Typography>
              <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
                <Typography variant="body1" paragraph>
                  <strong>Tratamentos com maior taxa de faltas:</strong>
                </Typography>
                <ul>
                  {(
                    dados.estatisticas.tratamentosMaisFaltados || [
                      "Dados insuficientes",
                    ]
                  )
                    .slice(0, 5)
                    .map((tratamento, idx) => (
                      <li key={idx}>
                        <Typography variant="body1">{tratamento}</Typography>
                      </li>
                    ))}
                </ul>
                <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                  <strong>Conclusão:</strong>{" "}
                  {dados.estatisticas.tratamentosMaisFaltados &&
                  dados.estatisticas.tratamentosMaisFaltados.length > 0 &&
                  dados.estatisticas.tratamentosMaisFaltados[0] !==
                    "Dados insuficientes"
                    ? `A análise revela que certos tipos de tratamento apresentam taxas de faltas superiores. Isto pode estar relacionado com a duração das sessões, complexidade dos procedimentos, desconforto associado, ou características específicas dos pacientes que necessitam destes tratamentos.`
                    : "Dados insuficientes para uma análise conclusiva sobre padrões de faltas por tipo de tratamento."}
                </Typography>
                <Typography variant="body1">
                  <strong>Recomendação:</strong>{" "}
                  {dados.estatisticas.tratamentosMaisFaltados &&
                  dados.estatisticas.tratamentosMaisFaltados.length > 0 &&
                  dados.estatisticas.tratamentosMaisFaltados[0] !==
                    "Dados insuficientes"
                    ? "Considere implementar estratégias específicas para os tratamentos mais faltados, como sessões educativas sobre a importância do tratamento, acompanhamento mais próximo destes pacientes, ou ajustes na duração e frequência das sessões para melhorar a adesão."
                    : "Continue a monitorizar os dados para identificar tendências emergentes."}
                </Typography>
              </Box>
            </Paper>

            {/* A listagem nominal. Fica no fim porque é o detalhe que
                explica os agregados acima — e é daqui que se telefona. */}
            <ListaFaltas
              faltas={dados.faltas}
              nomeClinica={dados.clinica?.nome}
              periodo={
                dados.periodo?.inicio && dados.periodo?.fim
                  ? `${dados.periodo.inicio} a ${dados.periodo.fim}`
                  : null
              }
              mostrarUnidade={
                (dados.estatisticas?.faltasPorUnidade?.length || 0) > 1
              }
            />

            {/* Recomendações — ver `RECOMENDACOES_IA_ATIVAS` no topo */}
            {RECOMENDACOES_IA_ATIVAS && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Recomendações da IA para Redução de Faltas
              </Typography>

              {/* Loading state */}
              {loadingRecomendacoes && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    p: 4,
                  }}
                >
                  <CircularProgress size={40} />
                  <Typography
                    variant="body2"
                    sx={{ mt: 2, color: "text.secondary" }}
                  >
                    Gerando recomendações personalizadas com IA...
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, color: "text.secondary" }}
                  >
                    Isso pode levar até 3 minutos
                  </Typography>
                </Box>
              )}

              {/* Error state */}
              {errorRecomendacoes && !loadingRecomendacoes && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {errorRecomendacoes}
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      component="span"
                      sx={{
                        color: "primary.main",
                        cursor: "pointer",
                        textDecoration: "underline",
                        fontSize: "0.875rem",
                      }}
                      onClick={() => gerarRecomendacoesIA(dados.estatisticas)}
                    >
                      Tentar novamente
                    </Typography>
                  </Box>
                </Alert>
              )}

              {/* Success state */}
              {recomendacoes && !loadingRecomendacoes && (
                <Box
                  sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}
                >
                  {recomendacoes.split("\n").map((linha, index) =>
                    linha.trim() ? (
                      <Typography key={index} paragraph>
                        {linha}
                      </Typography>
                    ) : (
                      <Box key={index} sx={{ height: 16 }} />
                    )
                  )}
                </Box>
              )}

              {/* Initial state - nada foi carregado ainda */}
              {!recomendacoes &&
                !loadingRecomendacoes &&
                !errorRecomendacoes && (
                  <Box
                    sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                    >
                      As recomendações serão geradas automaticamente...
                    </Typography>
                  </Box>
                )}
            </Paper>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}

export default RelatorioFaltasAgendaTratamento;
